import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  MessageSquare,
  Shield,
  Smartphone,
  Heart,
  Compass,
  Sparkles,
  Users,
  Calendar,
  Mail,
  School,
  Building,
  MapPin,
  Clock,
  CheckCircle2,
  Target,
  TrendingUp,
  Award,
  Quote,
  Instagram,
  GraduationCap
} from 'lucide-react';

const Landing = () => {
  const [volunteerForm, setVolunteerForm] = useState({
    name: '',
    institution: '',
    email: '',
    keyClub: '',
    location: ''
  });

  const [facilityForm, setFacilityForm] = useState({
    centerName: '',
    location: '',
    coordinatorName: '',
    coordinatorEmail: '',
    coordinatorPhone: '',
    workshopSlot: ''
  });

  const [showVolunteerSuccess, setShowVolunteerSuccess] = useState(false);
  const [showFacilitySuccess, setShowFacilitySuccess] = useState(false);

  const smoothScrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleVolunteerSubmit = (e) => {
    e.preventDefault();
    setShowVolunteerSuccess(true);
    setTimeout(() => setShowVolunteerSuccess(false), 6000);
    setVolunteerForm({ name: '', institution: '', email: '', keyClub: '', location: '' });
  };

  const handleFacilitySubmit = (e) => {
    e.preventDefault();
    setShowFacilitySuccess(true);
    setTimeout(() => setShowFacilitySuccess(false), 6000);
    setFacilityForm({
      centerName: '',
      location: '',
      coordinatorName: '',
      coordinatorEmail: '',
      coordinatorPhone: '',
      workshopSlot: ''
    });
  };

  const curriculumData = [
    {
      icon: MessageSquare,
      title: 'Communication Ecosystems',
      points: [
        'Writing and managing digital electronic mail',
        'Sending SMS text strings and messaging',
        'Contact directory archiving and organization',
        'Composing digital letters to family members'
      ]
    },
    {
      icon: Shield,
      title: 'Digital Defenses: Security Framework',
      points: [
        'Understanding information theft mechanics',
        'Identifying Vishing (voice scams) attacks',
        'Recognizing Smishing (text scams) patterns',
        'Detecting Phishing (email scams) attempts'
      ]
    },
    {
      icon: Smartphone,
      title: 'Device Optimization Hacks',
      points: [
        'Adjusting native phone layout configurations',
        'Enlarging system visibility and text sizing',
        'Configuring voice typing and dictation tools',
        'Managing smartphone photo album systems'
      ]
    },
    {
      icon: Heart,
      title: 'Health & Medical Utilities',
      points: [
        'Securely accessing online patient portals',
        'Tracking and reviewing laboratory charts',
        'Setting up digital medication reminders',
        'Accessing telehealth video consultation links'
      ]
    },
    {
      icon: Compass,
      title: 'Everyday Practical Toolkits',
      points: [
        'Mastering native GPS and mapping software',
        'Analyzing real-time weather data applications',
        'Building shared notes and task lists',
        'Creating and managing grocery tallies'
      ]
    },
    {
      icon: Sparkles,
      title: 'Group Entertainment & Enrichment',
      points: [
        'Navigating digital book and audio collections',
        'Streaming music via Spotify and other platforms',
        'Setting up media casting to TV devices',
        'Virtual memory-lane trips using Google Earth'
      ]
    }
  ];

  const timelineData = [
    {
      date: 'January 17th',
      title: 'Scope and Safety Alignment Meeting',
      description: 'Initial coordination with facility coordinators to establish operational framework'
    },
    {
      date: 'Late January',
      title: 'Device Foundations',
      description: 'Core iOS & Android ecosystem configuration and setup training'
    },
    {
      date: 'February 14th',
      title: 'Digital Defenses Course Launch',
      description: 'Introduction to identifying and mitigating modern digital scams'
    },
    {
      date: 'March 14th',
      title: 'Active Correspondence Workshop',
      description: 'Hands-on training in digital messaging and communication platforms'
    },
    {
      date: 'April 11th - May 9th',
      title: 'Practical App Deployments',
      description: 'Native application training and accessibility scaling techniques'
    }
  ];

  const internsData = [
    {
      name: 'Harjot Kaur',
      team: 'Team India',
      role: 'Intern'
    },
    {
      name: 'Tanveer',
      team: 'Team India',
      role: 'Intern'
    }
  ];

  const leadershipData = [
    {
      role: 'Founder & Chief Executive Officer (CEO)',
      name: 'Vaanya Tiwari',
      description: 'Founder and operational leader directing global scaling across the US, India, and Uganda. Oversees international operations, strategic planning, and expansion of the network to over 10 chapters, establishing institutional partnerships with global networks and public entities',
      image: 'https://customer-assets.emergentagent.com/job_volunteer-connect-48/artifacts/68wckeip_Screenshot%202026-06-14%20at%2010.37.46%E2%80%AFPM.png'
    },
    {
      role: 'Co-Founder & Chief Financial Officer (CFO)',
      name: 'Ariel He-Wang',
      description: 'Manages the financial framework, resource allocation, and budget modeling. Structures capital and financial pipelines required to sustain multi-city and international regional chapters, ensuring fiscal accountability and compliance as the organization scales',
      image: 'https://customer-assets.emergentagent.com/job_volunteer-connect-48/artifacts/15t1i690_IMG_5714.jpeg'
    },
    {
      role: 'Co-Founder & Chief Marketing Officer (CMO)',
      name: 'Sritha Veeraiah',
      description: 'Directs global marketing strategies, media presence, and brand positioning. Manages public outreach campaigns and audience engagement metrics to promote digital safety education across regional networks and international chapter expansion',
      image: 'https://customer-assets.emergentagent.com/job_volunteer-connect-48/artifacts/i0b7uli4_IMG_5713.jpeg'
    },
    {
      role: 'Chief Operating Officer (COO)',
      name: 'Niyati Chopra',
      description: 'Manages operational infrastructure, logistical timelines, and cross-border workflows. Coordinates project management across 10+ global chapters, ensuring deployment of curriculum and seminars in senior care facilities, adult schools, and international universities',
      image: 'https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/vrz7jzhe_1784867903674_temp..JPG'
    },
    {
      role: 'Executive Creative Director (ECD)',
      name: 'Arisha Bhattacharya',
      description: 'Directs visual identity, digital branding, and instructional curriculum design. Oversees development of technical and safety presentations used across public lectures, international seminars, and localized workshops, ensuring unified branding',
      image: 'https://customer-assets.emergentagent.com/job_volunteer-connect-48/artifacts/s3ah2dve_IMG_5712.jpeg'
    }
  ];

  return (
    <div className="landing-page-professional">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 navbar-professional">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_volunteer-connect-48/artifacts/lqnhc8xg_Screenshot%202026-05-23%20175642.png" 
                alt="Connect & Confident Logo" 
                className="logo-emblem"
              />
              <h1 className="text-xl font-bold nav-title-professional">Connect & Confident</h1>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => smoothScrollTo('about')} className="nav-link-professional">About Our Purpose</button>
              <button onClick={() => smoothScrollTo('curriculum')} className="nav-link-professional">Curriculum</button>
              <button onClick={() => smoothScrollTo('timeline')} className="nav-link-professional">Operational Timeline</button>
              <button onClick={() => smoothScrollTo('leadership')} className="nav-link-professional">Leadership Team</button>
              <button onClick={() => smoothScrollTo('partners')} className="nav-link-professional">Partners</button>
              <a 
                href="https://instagram.com/connect_confident" 
                target="_blank" 
                rel="noopener noreferrer"
                className="nav-instagram-link"
                title="Follow us on Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <Button
                onClick={() => smoothScrollTo('registration')}
                className="cta-button-professional"
              >
                Get Involved
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section-professional">
        <div className="max-w-7xl mx-auto px-6 py-32 text-center">
          <div className="mb-8">
            <h1 className="hero-title-professional mb-6">
              Bridging the Generational Digital Divide
            </h1>
            <p className="hero-subtitle-professional max-w-4xl mx-auto">
              Empowering older adults with the digital literacy, communication confidence, and cyber safety tools required to navigate a modern world with complete peace of mind.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              onClick={() => smoothScrollTo('registration')}
              size="lg"
              className="cta-primary-professional"
            >
              <Users className="mr-2 h-5 w-5" />
              Register for a Workshop
            </Button>
            <Button
              onClick={() => smoothScrollTo('registration')}
              size="lg"
              variant="outline"
              className="cta-secondary-professional"
            >
              <School className="mr-2 h-5 w-5" />
              Volunteer Information
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-8 metrics-banner-professional">
            <div className="metric-card-professional">
              <div className="metric-number-professional">5,000+</div>
              <div className="metric-label-professional">Individuals Globally</div>
            </div>
            <div className="metric-card-professional">
              <div className="metric-number-professional">55,000+</div>
              <div className="metric-label-professional">Taught Digital Literacy</div>
            </div>
            <div className="metric-card-professional">
              <div className="metric-number-professional">12+</div>
              <div className="metric-label-professional">Chapters</div>
            </div>
            <div className="metric-card-professional">
              <div className="metric-number-professional">100%</div>
              <div className="metric-label-professional">Student-Driven Initiative</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us & Mission Purpose */}
      <section className="about-section-professional" id="about">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <h2 className="section-title-professional">About Our Purpose</h2>
            <p className="section-subtitle-professional mt-4">
              Connect & Confident was founded to address a critical gap in modern society: the technological isolation experienced by our senior community members. Through structured, compassionate instruction delivered by dedicated student volunteers, we restore digital independence and rebuild confidence in an increasingly connected world.
            </p>
          </div>

          <Card className="blockquote-card">
            <CardContent className="p-8">
              <Quote className="blockquote-icon" />
              <blockquote className="blockquote-text">
                "True digital literacy is more than teaching mechanics; it is about restoring independence, securing privacy, and replacing tech anxiety with meaningful community connection."
              </blockquote>
              <cite className="blockquote-cite">— Team Connect & Confident</cite>
            </CardContent>
          </Card>

          <div className="contact-section">
            <Card className="contact-card">
              <CardContent className="p-6 text-center">
                <Mail className="contact-icon" />
                <h3 className="contact-title">Get in Touch</h3>
                <p className="contact-text">
                  For partnership opportunities, program inquiries, or general questions, please reach out to our team directly at:
                </p>
                <a href="mailto:ConnectConfidentinfo@gmail.com" className="contact-email">
                  ConnectConfidentinfo@gmail.com
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Curriculum Grid */}
      <section className="curriculum-section-professional" id="curriculum">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <h2 className="section-title-professional">The Interactive Curriculum Suite</h2>
            <p className="section-subtitle-professional">Standardized learning tracks designed for comprehensive digital enablement</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {curriculumData.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card key={index} className="curriculum-card-professional">
                  <CardHeader>
                    <div className="curriculum-icon-wrapper-professional mb-4">
                      <IconComponent className="h-7 w-7" />
                    </div>
                    <CardTitle className="curriculum-card-title-professional">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {item.points.map((point, idx) => (
                        <li key={idx} className="curriculum-point-professional">
                          <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline & Media */}
      <section className="timeline-section-professional" id="timeline">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <h2 className="section-title-professional">Milestone Operations & Media Feed</h2>
            <p className="section-subtitle-professional">Charting our operational path from conception to community impact</p>
          </div>

          <div className="timeline-container-professional">
            {timelineData.map((milestone, index) => (
              <div key={index} className="timeline-item-professional">
                <div className="timeline-marker-professional">
                  <Calendar className="h-6 w-6" />
                </div>
                <Card className="timeline-card-professional">
                  <CardHeader>
                    <Badge className="w-fit mb-2 badge-date-professional">{milestone.date}</Badge>
                    <CardTitle className="timeline-card-title-professional">{milestone.title}</CardTitle>
                    <CardDescription className="timeline-card-desc-professional">{milestone.description}</CardDescription>
                  </CardHeader>
                </Card>
              </div>
            ))}
          </div>

          {/* Photo Gallery */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-center mb-8 photo-gallery-title-professional">Workshop Documentation Archive</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="photo-frame-professional">
                <img 
                  src="https://customer-assets.emergentagent.com/job_volunteer-connect-48/artifacts/fldrbwxv_Screenshot%202026-06-14%20at%204.26.14%E2%80%AFPM.png" 
                  alt="Seniors learning digital skills"
                  className="photo-image-professional"
                />
              </div>
              <div className="photo-frame-professional">
                <img 
                  src="https://customer-assets.emergentagent.com/job_volunteer-connect-48/artifacts/yq0p92d0_Screenshot%202026-06-14%20at%204.27.00%E2%80%AFPM.png" 
                  alt="Student volunteer teaching smartphone skills"
                  className="photo-image-professional"
                />
              </div>
              <div className="photo-frame-professional">
                <img 
                  src="https://customer-assets.emergentagent.com/job_volunteer-connect-48/artifacts/uu5shfvb_Screenshot%202026-06-14%20at%204.27.43%E2%80%AFPM.png" 
                  alt="Hands-on device training session"
                  className="photo-image-professional"
                />
              </div>
              <div className="photo-frame-professional">
                <img 
                  src="https://customer-assets.emergentagent.com/job_volunteer-connect-48/artifacts/iza3kn8o_Screenshot%202026-06-14%20at%204.28.18%E2%80%AFPM.png" 
                  alt="Senior and volunteer together"
                  className="photo-image-professional"
                />
              </div>
              <div className="photo-frame-professional">
                <img 
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/s82bylfd_IMG_3471.webp" 
                  alt="Ivy Living seniors with workshop gifts"
                  className="photo-image-professional"
                />
              </div>
              <div className="photo-frame-professional">
                <img 
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/u7125o39_cc26cbe6-2ce3-44bb-b877-9cef523a87e7.webp" 
                  alt="India workshop - students in classroom"
                  className="photo-image-professional"
                />
              </div>
              <div className="photo-frame-professional">
                <img 
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/o4mnevqw_4c28f7aa-de9c-40c3-ad4a-5a3b3e7a58e1.webp" 
                  alt="India workshop - group session"
                  className="photo-image-professional"
                />
              </div>
              <div className="photo-frame-professional">
                <img 
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/815rzq25_1d9387bb-f08d-402f-a311-b9581a6def60.JPG" 
                  alt="India workshop - assembly presentation"
                  className="photo-image-professional"
                />
              </div>
              <div className="photo-frame-professional">
                <img 
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/jmth4ecl_IMG_4677.webp" 
                  alt="Ivy Living workshop presentation"
                  className="photo-image-professional"
                />
              </div>
              <div className="photo-frame-professional">
                <img 
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/67t8oos1_IMG_4672.webp" 
                  alt="Volunteer with Ivy Living seniors"
                  className="photo-image-professional"
                />
              </div>
              <div className="photo-frame-professional">
                <img 
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/cxajei5a_IMG_3482.webp" 
                  alt="Delivering gift bags to seniors"
                  className="photo-image-professional"
                />
              </div>
              <div className="photo-frame-professional">
                <img 
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/ng6abr44_f458ad22-30d0-472b-a3ee-583487f8ef55.JPG" 
                  alt="India Connect & Confident classroom session"
                  className="photo-image-professional"
                />
              </div>
            </div>
          </div>

          {/* Video Player */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-center mb-8 photo-gallery-title-professional">Live Workshop Archive</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <div className="video-container-professional">
                <video className="video-player-professional" controls>
                  <source src="https://customer-assets.emergentagent.com/job_volunteer-connect-48/artifacts/y4bxjk38_IMG_5133%20%281%29.mov" type="video/quicktime" />
                  <source src="https://customer-assets.emergentagent.com/job_volunteer-connect-48/artifacts/y4bxjk38_IMG_5133%20%281%29.mov" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <p className="video-caption-professional">US Workshop: Ivy Living Senior Session</p>
              </div>
              <div className="video-container-professional">
                <video className="video-player-professional" controls>
                  <source src="https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/ekbbolcq_IMG_5895.MOV" type="video/quicktime" />
                  <source src="https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/ekbbolcq_IMG_5895.MOV" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <p className="video-caption-professional">US Workshop: Community Impact Session</p>
              </div>
              <div className="video-container-professional">
                <video className="video-player-professional" controls>
                  <source src="https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/xszw2igs_47fda62b-4b96-444c-a0e3-cd628a93ebe7.MP4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <p className="video-caption-professional">Team India: Live Workshop Session</p>
              </div>
              <div className="video-container-professional">
                <video className="video-player-professional" controls>
                  <source src="https://customer-assets-eiarnc6j.emergentagent.net/job_volunteer-connect-48/artifacts/340kmpp3_35de865a-9b18-4aca-991c-72404e0e4159.MP4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <p className="video-caption-professional">Team India: Digital Literacy Workshop</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Panel */}
      <section className="leadership-section-professional" id="leadership">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <h2 className="section-title-professional">Leadership Team Grid</h2>
            <p className="section-subtitle-professional">Executive profiles and organizational structure</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {leadershipData.map((leader, index) => (
              <Card key={index} className="leadership-card-professional">
                <CardHeader>
                  <div className="leadership-avatar-professional">
                    {leader.image ? (
                      <img 
                        src={leader.image} 
                        alt={leader.name}
                        className="leadership-profile-image"
                      />
                    ) : (
                      <Users className="h-12 w-12" />
                    )}
                  </div>
                  <CardTitle className="leadership-name-professional">{leader.name}</CardTitle>
                  <Badge className="badge-role-professional">{leader.role}</Badge>
                  <CardDescription className="leadership-desc-professional">{leader.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Interns Section */}
      <section className="interns-section-professional" id="interns">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="section-title-professional">Our Interns</h2>
            <p className="section-subtitle-professional">Rising talent driving our global mission forward</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {internsData.map((intern, index) => (
              <Card key={index} className="intern-card-professional">
                <CardHeader className="text-center">
                  <div className="intern-avatar-professional">
                    <GraduationCap className="h-10 w-10" />
                  </div>
                  <CardTitle className="intern-name-professional">{intern.name}</CardTitle>
                  <Badge className="badge-team-professional">{intern.team}</Badge>
                  <CardDescription className="intern-role-professional">{intern.role}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Global Partners & Collaborators */}
      <section className="partners-section-professional" id="partners">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <h2 className="section-title-professional">Our Global Partners & Collaborators</h2>
            <p className="section-subtitle-professional">
              We believe bridging the digital divide requires global teamwork. Connect & Confident proudly collaborates with leading international networks, civic clubs, and public educational institutions to maximize our reach and scale our digital literacy initiatives across the US, India, and Uganda.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="partner-card-professional">
              <CardHeader>
                <div className="partner-icon-wrapper">
                  <Target className="h-10 w-10" />
                </div>
                <CardTitle className="partner-card-title">Rotary International Clubs</CardTitle>
                <CardDescription className="partner-card-desc">
                  Partnering with local and international Rotary chapters allows us to deeply root our digital literacy initiatives in regional communities. Through their extensive civic network, we host educational seminars, access community spaces, and directly connect with older generations who benefit most from technology mentorship.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="partner-card-professional">
              <CardHeader>
                <div className="partner-icon-wrapper">
                  <Building className="h-10 w-10" />
                </div>
                <CardTitle className="partner-card-title">Indian Government Institutions & Public Colleges</CardTitle>
                <CardDescription className="partner-card-desc">
                  To combat the rise of digital fraud and cross-border cyber scams, we collaborate directly with government institutions, public universities, and private colleges across India. These institutional partnerships allow us to host large-scale informational lectures and seminars, creating safer digital spaces for families.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="partner-card-professional">
              <CardHeader>
                <div className="partner-icon-wrapper">
                  <Award className="h-10 w-10" />
                </div>
                <CardTitle className="partner-card-title">E-NABLE Network</CardTitle>
                <CardDescription className="partner-card-desc">
                  Our critical operational link in Uganda is built on a shared humanitarian mission with the global e-NABLE community. By aligning with their established network, we have successfully expanded our digital literacy infrastructure into East Africa, ensuring technology education reaches underserved youth and communities.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="partner-card-professional">
              <CardHeader>
                <div className="partner-icon-wrapper">
                  <Heart className="h-10 w-10" />
                </div>
                <CardTitle className="partner-card-title">Adult Education Schools & Senior Care Facilities</CardTitle>
                <CardDescription className="partner-card-desc">
                  At the grassroots level, we partner with regional adult education schools and elderly care facilities. We design and deliver custom, empathetic curricula tailored to adult learners, giving senior citizens the practical, day-to-day tech skills they need to stay safely connected with their loved ones.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Registration Portal */}
      <section className="registration-section-professional" id="registration">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <h2 className="section-title-professional">Registration & Inquiry Portal</h2>
            <p className="section-subtitle-professional">Connect with us to participate or bring our program to your community</p>
          </div>

          <Tabs defaultValue="volunteer" className="registration-tabs-professional">
            <TabsList className="grid w-full grid-cols-2 tab-list-professional">
              <TabsTrigger value="volunteer" className="tab-trigger-professional">Volunteer Onboarding</TabsTrigger>
              <TabsTrigger value="facility" className="tab-trigger-professional">Bring Program to a Facility</TabsTrigger>
            </TabsList>

            <TabsContent value="volunteer" className="tab-content-professional">
              <Card className="registration-card-professional">
                <CardHeader>
                  <CardTitle className="registration-card-title-professional">Volunteer Onboarding Application</CardTitle>
                  <CardDescription className="registration-card-desc-professional">
                    Join our dedicated network of student volunteers making a meaningful difference in senior digital literacy.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {showVolunteerSuccess ? (
                    <div className="success-message-professional">
                      <CheckCircle2 className="h-16 w-16 mb-4 success-icon" />
                      <h3 className="success-title-professional">Thank You for Connecting!</h3>
                      <p className="success-text-professional">
                        We have received your volunteer application. Our team will review your information and reach out to you within 48 hours with next steps.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleVolunteerSubmit} className="space-y-6">
                      <div className="form-group-professional">
                        <Label htmlFor="volunteer-name" className="form-label-professional">
                          <Users className="h-4 w-4 mr-2" />
                          Full Name
                        </Label>
                        <Input
                          id="volunteer-name"
                          value={volunteerForm.name}
                          onChange={(e) => setVolunteerForm({ ...volunteerForm, name: e.target.value })}
                          required
                          className="form-input-professional"
                          placeholder="Enter your full name"
                        />
                      </div>

                      <div className="form-group-professional">
                        <Label htmlFor="volunteer-institution" className="form-label-professional">
                          <School className="h-4 w-4 mr-2" />
                          Academic Institution
                        </Label>
                        <Input
                          id="volunteer-institution"
                          value={volunteerForm.institution}
                          onChange={(e) => setVolunteerForm({ ...volunteerForm, institution: e.target.value })}
                          required
                          className="form-input-professional"
                          placeholder="Your school or university"
                        />
                      </div>

                      <div className="form-group-professional">
                        <Label htmlFor="volunteer-email" className="form-label-professional">
                          <Mail className="h-4 w-4 mr-2" />
                          Contact Email
                        </Label>
                        <Input
                          id="volunteer-email"
                          type="email"
                          value={volunteerForm.email}
                          onChange={(e) => setVolunteerForm({ ...volunteerForm, email: e.target.value })}
                          required
                          className="form-input-professional"
                          placeholder="your.email@example.com"
                        />
                      </div>

                      <div className="form-group-professional">
                        <Label htmlFor="volunteer-keyclub" className="form-label-professional">
                          Key Club Affiliation
                        </Label>
                        <Input
                          id="volunteer-keyclub"
                          value={volunteerForm.keyClub}
                          onChange={(e) => setVolunteerForm({ ...volunteerForm, keyClub: e.target.value })}
                          className="form-input-professional"
                          placeholder="Key Club name (if applicable)"
                        />
                      </div>

                      <div className="form-group-professional">
                        <Label htmlFor="volunteer-location" className="form-label-professional">
                          <MapPin className="h-4 w-4 mr-2" />
                          Preferred Chapter Location
                        </Label>
                        <Select
                          value={volunteerForm.location}
                          onValueChange={(value) => setVolunteerForm({ ...volunteerForm, location: value })}
                          required
                        >
                          <SelectTrigger className="form-input-professional">
                            <SelectValue placeholder="Select your location" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dublin">Dublin</SelectItem>
                            <SelectItem value="suisun">Suisun</SelectItem>
                            <SelectItem value="fairfield">Fairfield</SelectItem>
                            <SelectItem value="india-punjab">India - Punjab (Kapurthala)</SelectItem>
                            <SelectItem value="new-chapter">Help start a new chapter in your area</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Button type="submit" className="form-submit-button-professional w-full">
                        <TrendingUp className="mr-2 h-5 w-5" />
                        Submit Application
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="facility" className="tab-content-professional">
              <Card className="registration-card-professional">
                <CardHeader>
                  <CardTitle className="registration-card-title-professional">Facility Partnership Inquiry</CardTitle>
                  <CardDescription className="registration-card-desc-professional">
                    Bring comprehensive digital literacy training to your senior living community.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {showFacilitySuccess ? (
                    <div className="success-message-professional">
                      <CheckCircle2 className="h-16 w-16 mb-4 success-icon" />
                      <h3 className="success-title-professional">Partnership Inquiry Received!</h3>
                      <p className="success-text-professional">
                        Thank you for your interest in bringing Connect & Confident to your facility. Our partnership coordination team will contact you within 48 hours to discuss implementation details.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleFacilitySubmit} className="space-y-6">
                      <div className="form-group-professional">
                        <Label htmlFor="center-name" className="form-label-professional">
                          <Building className="h-4 w-4 mr-2" />
                          Care Center Name
                        </Label>
                        <Input
                          id="center-name"
                          value={facilityForm.centerName}
                          onChange={(e) => setFacilityForm({ ...facilityForm, centerName: e.target.value })}
                          required
                          className="form-input-professional"
                          placeholder="e.g., Ivy Living Park"
                        />
                      </div>

                      <div className="form-group-professional">
                        <Label htmlFor="center-location" className="form-label-professional">
                          <MapPin className="h-4 w-4 mr-2" />
                          Regional Location
                        </Label>
                        <Input
                          id="center-location"
                          value={facilityForm.location}
                          onChange={(e) => setFacilityForm({ ...facilityForm, location: e.target.value })}
                          required
                          className="form-input-professional"
                          placeholder="City, State"
                        />
                      </div>

                      <div className="form-group-professional">
                        <Label htmlFor="coordinator-name" className="form-label-professional">
                          <Users className="h-4 w-4 mr-2" />
                          Coordinator Name
                        </Label>
                        <Input
                          id="coordinator-name"
                          value={facilityForm.coordinatorName}
                          onChange={(e) => setFacilityForm({ ...facilityForm, coordinatorName: e.target.value })}
                          required
                          className="form-input-professional"
                          placeholder="Primary contact person"
                        />
                      </div>

                      <div className="form-group-professional">
                        <Label htmlFor="coordinator-email" className="form-label-professional">
                          <Mail className="h-4 w-4 mr-2" />
                          Coordinator Email
                        </Label>
                        <Input
                          id="coordinator-email"
                          type="email"
                          value={facilityForm.coordinatorEmail}
                          onChange={(e) => setFacilityForm({ ...facilityForm, coordinatorEmail: e.target.value })}
                          required
                          className="form-input-professional"
                          placeholder="coordinator@facility.com"
                        />
                      </div>

                      <div className="form-group-professional">
                        <Label htmlFor="coordinator-phone" className="form-label-professional">
                          Coordinator Phone
                        </Label>
                        <Input
                          id="coordinator-phone"
                          type="tel"
                          value={facilityForm.coordinatorPhone}
                          onChange={(e) => setFacilityForm({ ...facilityForm, coordinatorPhone: e.target.value })}
                          required
                          className="form-input-professional"
                          placeholder="(555) 123-4567"
                        />
                      </div>

                      <div className="form-group-professional">
                        <Label htmlFor="workshop-slot" className="form-label-professional">
                          <Clock className="h-4 w-4 mr-2" />
                          Preferred Workshop Slot
                        </Label>
                        <Select
                          value={facilityForm.workshopSlot}
                          onValueChange={(value) => setFacilityForm({ ...facilityForm, workshopSlot: value })}
                          required
                        >
                          <SelectTrigger className="form-input-professional">
                            <SelectValue placeholder="Select a time slot" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="saturday-morning">Saturday Morning (9:00 - 11:00 AM)</SelectItem>
                            <SelectItem value="saturday-afternoon">Saturday Afternoon (2:00 - 4:00 PM)</SelectItem>
                            <SelectItem value="sunday-morning">Sunday Morning (10:00 AM - 12:00 PM)</SelectItem>
                            <SelectItem value="flexible">Flexible (We'll discuss)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Button type="submit" className="form-submit-button-professional w-full">
                        <TrendingUp className="mr-2 h-5 w-5" />
                        Submit Partnership Inquiry
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Team Inquiry Card */}
          <Card className="announcement-card-professional mt-12">
            <CardHeader className="text-center">
              <div className="announcement-icon-professional">
                <Users className="h-12 w-12" />
              </div>
              <CardTitle className="announcement-title-professional">
                Join Our Team
              </CardTitle>
              <CardDescription className="announcement-desc-professional">
                Interested in becoming part of our leadership or volunteer network? We'd love to hear from you! Fill out the inquiry form above or reach out directly to our team at:
                <br />
                <a href="mailto:ConnectConfidentinfo@gmail.com" className="contact-email mt-3 inline-block">
                  ConnectConfidentinfo@gmail.com
                </a>
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-section-professional">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_volunteer-connect-48/artifacts/lqnhc8xg_Screenshot%202026-05-23%20175642.png" 
                alt="Connect & Confident Logo" 
                className="footer-logo"
              />
              <h3 className="text-xl font-bold footer-title">Connect & Confident</h3>
            </div>
            <p className="footer-tagline-professional mb-6">
              Bridging the Generational Digital Divide Through Student-Led Education
            </p>

            <a 
              href="https://instagram.com/connect_confident" 
              target="_blank" 
              rel="noopener noreferrer"
              className="instagram-follow-badge"
            >
              <Instagram className="h-5 w-5" />
              <span>Follow us on Instagram: @connect_confident</span>
            </a>

            <div className="footer-links-professional mt-6">
              <button onClick={() => smoothScrollTo('about')} className="footer-link-professional">About</button>
              <span className="footer-separator-professional">•</span>
              <button onClick={() => smoothScrollTo('curriculum')} className="footer-link-professional">Curriculum</button>
              <span className="footer-separator-professional">•</span>
              <button onClick={() => smoothScrollTo('timeline')} className="footer-link-professional">Timeline</button>
              <span className="footer-separator-professional">•</span>
              <button onClick={() => smoothScrollTo('leadership')} className="footer-link-professional">Leadership</button>
            </div>
            <p className="footer-contact-professional mt-4">
              <Mail className="inline h-4 w-4 mr-2" />
              <a href="mailto:ConnectConfidentinfo@gmail.com">ConnectConfidentinfo@gmail.com</a>
            </p>
            <p className="footer-copyright-professional">
              © 2025 Connect & Confident. A Student-Led Non-Profit Initiative.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;